import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  AllEmployeeGroupsDocument,
  CreateEmployeeGroupDocument,
  EmployeeGroupRootPageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'
import app from 'modules/core/store/models/app'

interface Props {
  legalEntity: LegalEntity
  onClose: () => void
}

const CreateModal = ({ onClose, legalEntity }: Props) => {
  const form = useLazyRef(() => new Form({}, { name: 'string' })).current
  const [loading, setLoading] = useState<boolean>(false)

  const [CreateEmployeeGroup] = useMutation(CreateEmployeeGroupDocument, {
    extractorFn: (data) => data.employeesMutations.createEmployeesEmployeeGroup,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    setLoading(true)
    const result = await CreateEmployeeGroup({
      variables: {
        name: form.get('name').value,
        legalEntityId: legalEntity.attributes.id,
      },
      refetchQueries: [
        {
          query: AllEmployeeGroupsDocument,
          variables: {
            legalEntityIds: [legalEntity.attributes.id],
          },
        },
        {
          query: EmployeeGroupRootPageDocument,
          variables: {
            legalEntityIds: [legalEntity.attributes.id],
          },
        },
      ],
    })
    const handler = handleResult(result, () => null)
    setLoading(false)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closePortal()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  return (
    <Modal
      illustration="group"
      title={i18n.t('settings.legal_entities.legal_entity.employee_group.create.title')}
      description={i18n.t('settings.legal_entities.legal_entity.employee_group.create.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button
          disabled={!form.get('name').value || loading}
          submit
          extra={{ 'data-intercom-target': 'settings-employee-group-create-modal-footer' }}
        >
          {i18n.t('settings.legal_entities.legal_entity.employee_group.create.submit')}
        </Button>
      }
    >
      <Text
        label={i18n.t('settings.legal_entities.legal_entity.employee_group.labels.name')}
        field={form.get('name')}
        extra={{ 'data-intercom-target': 'settings-employee-group-create-modal' }}
      />
    </Modal>
  )
}

export default observer(CreateModal)
